import { create } from 'zustand'
import { apiClient, refreshSession, setAccessToken } from '@/lib/api/client'
import type { CurrentUser, LoginResponse } from '@/lib/api/types'

interface AuthState {
  user: CurrentUser | null
  /** Permission codes as a Set — `usePermission` runs on every render, so O(1) matters. */
  permissions: Set<string>
  /** True until the initial silent-refresh has settled, so guards do not flash /login. */
  initialising: boolean
  mustChangePassword: boolean

  login: (email: string, password: string, mfaCode?: string) => Promise<LoginResponse>
  logout: () => Promise<void>
  /** Attempts a silent refresh on app start, using the HttpOnly cookie. */
  bootstrap: () => Promise<void>
  clear: () => void
}

/**
 * Auth state.
 *
 * Holds the access token in memory (inside the API client) and never in localStorage:
 * an XSS that can read localStorage can steal a session, while a token in a closure dies
 * with the tab. Persistence across reloads comes from the HttpOnly refresh cookie instead.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: new Set<string>(),
  initialising: true,
  mustChangePassword: false,

  login: async (email, password, mfaCode) => {
    const { data } = await apiClient.post<LoginResponse>('/v1/auth/login', {
      email,
      password,
      mfaCode,
    })
    setAccessToken(data.accessToken)
    set({
      user: data.user,
      permissions: new Set(data.user.permissions),
      mustChangePassword: data.mustChangePassword,
      initialising: false,
    })
    return data
  },

  logout: async () => {
    try {
      await apiClient.post('/v1/auth/logout')
    } catch {
      // Already invalid server-side; clearing locally is still the right outcome.
    }
    get().clear()
  },

  bootstrap: async () => {
    try {
      // Goes through the shared single-flight refresh rather than calling the endpoint
      // directly. Two concurrent refreshes spend the same rotating token and the second
      // one fails, which used to sign the user out on every page reload.
      const data = (await refreshSession()) as unknown as LoginResponse
      set({
        user: data.user,
        permissions: new Set(data.user.permissions),
        mustChangePassword: data.mustChangePassword,
      })
    } catch {
      // No valid cookie: a normal first visit, not an error worth surfacing.
      setAccessToken(null)
      set({ user: null, permissions: new Set() })
    } finally {
      set({ initialising: false })
    }
  },

  clear: () => {
    setAccessToken(null)
    set({ user: null, permissions: new Set(), mustChangePassword: false, initialising: false })
  },
}))

/** True when the user holds the permission. Super admins pass everything. */
export function hasPermission(permission: string | undefined): boolean {
  if (!permission) return true
  const { permissions, user } = useAuthStore.getState()
  return user?.roles.includes('SUPER_ADMIN') || permissions.has(permission)
}
